import { deletePhoto, moveItem, reorderPhotos } from '@auctisync/api-client';
import { Button, HelpTooltip } from '@auctisync/ui';
import { thumbUrl } from '@auctisync/shared';
import { INVENTORY_PHOTO_ROW_COLUMNS, type InventoryPhotoRow } from '../lib/rowColumns';
import { useEffect, useState, type ChangeEvent } from 'react';
import { compressImage } from '../lib/compressImage';
import { supabase } from '../lib/supabase';

type Photo = InventoryPhotoRow;

type PhotoManagerProps = {
  /** Item-owned photos. Pass this on the inventory page. */
  inventoryItemId?: string;
  /** Lot-owned photos. Pass this on the lot page. If both are passed, lot wins. */
  lotId?: string;
  tenantId: string;
};

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED = 'image/jpeg,image/png,image/webp,image/heic,image/gif';

/**
 * Photo manager for inventory items OR lots (Module #11.5 lot independence).
 *
 * Uploads route through the `upload-photo-to-r2` Edge Function which
 * proxies bytes to the R2 bucket and inserts the inventory_photos row.
 * Deletes and reorders go through `@auctisync/api-client` — the same rules
 * the Cataloging app uses. A reorder is ONE server call that renumbers the
 * whole list (`reorder_photos`); the old two-write swap could leave two photos
 * sharing a number, after which nothing would move.
 */
export function PhotoManager({ inventoryItemId, lotId, tenantId: _tenantId }: PhotoManagerProps) {
  const ownerColumn = lotId ? 'lot_id' : 'inventory_item_id';
  const ownerValue = lotId ?? inventoryItemId;

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ownerValue) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('inventory_photos')
        .select(INVENTORY_PHOTO_ROW_COLUMNS)
        .eq(ownerColumn, ownerValue)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setPhotos(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerColumn, ownerValue]);

  async function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    setProgress({ done: 0, total: files.length });

    const created: Photo[] = [];

    for (let i = 0; i < files.length; i += 1) {
      const raw = files[i];
      if (!raw) continue;

      if (raw.size > MAX_FILE_BYTES) {
        setError(`${raw.name}: file exceeds 10 MB limit.`);
        continue;
      }

      // Downsize in the browser before uploading — keeps R2 storage
      // small, upload fast, and downstream AI Edge Functions well
      // inside their CPU budget.
      const file = await compressImage(raw);

      const fd = new FormData();
      fd.append('file', file);
      if (lotId) fd.append('lot_id', lotId);
      if (inventoryItemId) fd.append('inventory_item_id', inventoryItemId);

      const { data, error: fnErr } = (await supabase.functions.invoke('upload-photo-to-r2', {
        body: fd,
      })) as {
        data: { photo?: Photo; error?: string; detail?: string } | null;
        error: { message?: string } | null;
      };
      if (fnErr || data?.error || !data?.photo) {
        setError(`${file.name}: ${data?.error ?? fnErr?.message ?? 'upload failed'}`);
      } else {
        created.push(data.photo);
      }
      setProgress({ done: i + 1, total: files.length });
    }

    if (created.length > 0) {
      setPhotos((prev) => [...prev, ...created]);
    }
    setUploading(false);
    event.target.value = '';
  }

  async function handleDelete(photo: Photo) {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    const res = await deletePhoto(supabase, { photoId: photo.id });
    if (!res.ok) {
      setError(`Could not delete photo (${res.error}).`);
      return;
    }
    setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
  }

  async function handleReorder(photo: Photo, direction: 'left' | 'right') {
    const idx = photos.findIndex((p) => p.id === photo.id);
    const next = moveItem(photos, idx, direction === 'left' ? idx - 1 : idx + 1);
    if (next.every((p, i) => p.id === photos[i]?.id)) return;
    const owner = lotId ? { lotId } : inventoryItemId ? { inventoryItemId } : null;
    if (!owner) return;
    try {
      await reorderPhotos(
        supabase,
        owner,
        next.map((p) => p.id),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reorder photos.');
      return;
    }
    setPhotos(next.map((p, i) => ({ ...p, sort_order: i })));
  }

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {photos.length === 0
              ? 'No photos yet.'
              : `${photos.length} photo${photos.length === 1 ? '' : 's'}`}
          </span>
          <HelpTooltip helpSlug="photo-upload-basics">
            JPEG, PNG, WebP, HEIC, or GIF. Up to 10 MB each. First photo is the primary.
          </HelpTooltip>
        </div>
        <label>
          <input
            type="file"
            accept={ACCEPTED}
            multiple
            disabled={uploading}
            onChange={(e) => {
              void handleFiles(e);
            }}
            style={{ display: 'none' }}
          />
          <Button type="button" variant="outline" disabled={uploading} asChild>
            <span style={{ cursor: 'pointer' }}>
              {uploading ? `Uploading ${progress.done}/${progress.total}…` : '+ Add photos'}
            </span>
          </Button>
        </label>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading photos…</div>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(10rem, 1fr))',
          }}
        >
          {photos.map((photo, idx) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              isPrimary={idx === 0}
              canMoveLeft={idx > 0}
              canMoveRight={idx < photos.length - 1}
              onDelete={() => {
                void handleDelete(photo);
              }}
              onMoveLeft={() => {
                void handleReorder(photo, 'left');
              }}
              onMoveRight={() => {
                void handleReorder(photo, 'right');
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoTile({
  photo,
  isPrimary,
  canMoveLeft,
  canMoveRight,
  onDelete,
  onMoveLeft,
  onMoveRight,
}: {
  photo: Photo;
  isPrimary: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  onDelete: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
}) {
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--surface-muted)',
        aspectRatio: '1 / 1',
      }}
    >
      <img
        src={thumbUrl(photo.display_url)}
        alt={photo.caption ?? 'Inventory photo'}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        loading="lazy"
      />
      {isPrimary && (
        <span
          style={{
            position: 'absolute',
            top: '0.35rem',
            left: '0.35rem',
            fontSize: '0.65rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '0.15rem 0.4rem',
            borderRadius: '0.25rem',
            background: 'var(--brand-primary)',
            color: 'var(--brand-primary-fg)',
          }}
        >
          Primary
        </span>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: '0.35rem',
          right: '0.35rem',
          display: 'flex',
          gap: '0.25rem',
        }}
      >
        <IconButton ariaLabel="Move left" onClick={onMoveLeft} disabled={!canMoveLeft}>
          ←
        </IconButton>
        <IconButton ariaLabel="Move right" onClick={onMoveRight} disabled={!canMoveRight}>
          →
        </IconButton>
        <IconButton ariaLabel="Delete photo" onClick={onDelete} variant="danger">
          ×
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  onClick,
  ariaLabel,
  disabled,
  variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
  variant?: 'danger';
}) {
  const bg = variant === 'danger' ? 'rgba(185, 28, 28, 0.85)' : 'rgba(17, 24, 39, 0.7)';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      style={{
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '0.25rem',
        border: 'none',
        background: bg,
        color: 'white',
        fontSize: '0.9rem',
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
